<template>
    <vx-chart-column-stacked
        legend
        :error="hasError"
        @error="chartError = true"
        :loading="loading"
        :data="dataComputed.data"
        loadingStyle="bars"
        :colors="dataComputed.colors"
        :axis="{
            x: {
                key: 'date',
                label: '',
                interval: 'day',
                ticks: 10,
                tickFormat: (value) => dayjs.utc(value).format('MM/DD'),
            },
            y: {
                key: 'count',
                rawKey: 'rawCount',
                ticks: 10,
                label: '',
                tickFormat: (value) => (Number.isInteger(value) ? formatCount(value) : ''),
            },
        }"
    >
        <template v-slot:title>
            <slot name="title"> Messages Sent by Day </slot>
        </template>

        <template v-slot:explainHeader>Messages Sent By Day</template>
        <template v-slot:explainContent>
            <p>Number of Messages (SMS & MMS) sent in the selected period of time distributed by Audience Type.</p>
            <vx-button href="/messaging/campaigns" size="sm" color="muted">
                Go to Campaigns
                <font-awesome-icon :icon="faArrowRight"></font-awesome-icon>
            </vx-button>
        </template>
    </vx-chart-column-stacked>
</template>
<script setup>
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxChartColumnStacked } from '@voxie/frontend-components';
import dayjs from '~/utils/dayjs';
import { ref, watch, computed } from 'vue';
import { analyticsClient } from '~/services';
import { formatCount } from '~/components/filters';
import { calculateRangeBetweenTwoDates, generateLegendColors, metricsInputValidator } from '~/utils/analytics';
import { useVersionedRequest } from '~/composables/useVersionedRequest';
import { audienceTypeLabel } from '~/constants/campaign';

const props = defineProps({
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
});

const chartError = ref(false);
const hasError = computed(() => {
    return Boolean(error.value) || chartError.value;
});

const computedRange = computed(() => {
    return calculateRangeBetweenTwoDates({
        startDate: props.fetchParams.range[0],
        endDate: props.fetchParams.range[1],
    });
});
const { loading, response, error, fetchData } = useVersionedRequest();
const dataComputed = computed(() => {
    const rawData = (response.value?.data?.data || []).sort((a, b) => b.audience_type.localeCompare(a.audience_type));

    const allValues = rawData
        .reduce((values, audienceType) => values.concat(audienceType.values), [])
        .sort((a, b) => b - a);
    const highestValue = allValues[0] || 0;
    const lowestValue = allValues[allValues.length - 1] || 0;

    // only computes a new count when difference is bigger than 300 times than lowest value
    const shouldComputeCount = highestValue - lowestValue > 300 * lowestValue;

    const groupedByDay = [];

    computedRange.value.forEach((date, index) => {
        if (index === computedRange.value.length - 1) {
            return;
        }

        const day = {
            date: date,
            data: [],
        };

        rawData.forEach((audienceType) => {
            const rawCount = audienceType.values[index];
            const count = shouldComputeCount ? Math.max(rawCount, Math.round(highestValue * 0.02)) : rawCount;

            day.data.push({
                label: audienceTypeLabel(audienceType.audience_type),
                date: new Date(date),
                rawCount: rawCount,
                count: count,
                changedCount: rawCount !== count,
            });
        });

        day.data = day.data.sort((a, b) => b.rawCount - a.rawCount);

        if (day.data.some((item) => item.changedCount) && !day.data.every((item) => item.changedCount)) {
            const highestInDay = day.data[0];
            if (!highestInDay.changedCount) {
                // * 2 because day.data includes it on the reducer sum
                const adjustedCount =
                    highestInDay.count * 2 - day.data.reduce((sum, current) => sum + current.count, 0);
                day.data[0].count = adjustedCount > 0 ? adjustedCount : highestInDay.count;
            }
        }

        groupedByDay.push(day);
    });

    const data = groupedByDay.reduce((data, day) => data.concat(day.data), []);

    return {
        data: data.sort((a, b) => a.label.localeCompare(b.label)),
        colors: generateLegendColors(
            rawData.map((audienceType) => audienceTypeLabel(audienceType.audience_type)) || []
        ).sort((a, b) => b.label.localeCompare(a.label)),
    };
});
watch(
    [() => props.fetchParams],
    () => {
        chartError.value = false;
        fetchData(
            analyticsClient.outboundMessagesTotalMessagesSentByAudienceType(Spark.state.currentTeam.id, {
                ...props.fetchParams,
                range: computedRange.value,
            })
        ).catch((e) => {
            console.error(e);
        });
    },
    { immediate: true, deep: true }
);
</script>
